import { create } from 'zustand';
import type { NFTData, MemeStore, MemeNFTElement, MemeTextElement, MemePreset, MemeViewport } from '../types';
import { DEFAULT_MEME_PRESET } from '../constants';
import { undoMiddleware } from './undoMiddleware';

function generateElementId(): string {
  return `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

const DEFAULT_VIEWPORT: MemeViewport = {
  zoom: 1,
  panX: 0,
  panY: 0,
};

const DEFAULT_TEXT_PLACEHOLDER = 'YOUR TEXT HERE';

import type { MemeState, MemeActions } from '../types';

type MemeBase = MemeState & MemeActions;

const MEME_TRACKED_KEYS: (keyof MemeBase)[] = [
  'canvasWidth', 'canvasHeight', 'activePreset',
  'backgroundImage', 'backgroundImageScale', 'backgroundImageOffset',
  'backgroundColor', 'nftElements', 'textElements', 'viewport',
];

export const useMemeStore = create<MemeStore>(undoMiddleware<MemeBase>(MEME_TRACKED_KEYS, (set, get) => ({
  canvasWidth: DEFAULT_MEME_PRESET.width,
  canvasHeight: DEFAULT_MEME_PRESET.height,
  activePreset: DEFAULT_MEME_PRESET,
  backgroundImage: null,
  backgroundImageScale: 1.0,
  backgroundImageOffset: { x: 0, y: 0 },
  backgroundColor: '#1a1a2e',
  nftElements: [],
  textElements: [],
  selectedElementId: null,
  selectedElementType: null,
  isDragging: false,
  isResizing: false,
  resizeHandle: null,
  viewport: DEFAULT_VIEWPORT,

  setPreset: (preset: MemePreset) => {
    const { canvasWidth: oldWidth, canvasHeight: oldHeight, nftElements, textElements } = get();
    const newWidth = preset.width;
    const newHeight = preset.height;

    // Calculate scale factors for repositioning elements
    const scaleX = newWidth / oldWidth;
    const scaleY = newHeight / oldHeight;

    // Reposition NFT elements to fit in the new canvas dimensions
    // Keep the scale unchanged - only adjust position
    const updatedNFTElements = nftElements.map((el) => ({
      ...el,
      position: {
        x: el.position.x * scaleX,
        y: el.position.y * scaleY,
      },
    }));

    // Reposition text elements as well
    const updatedTextElements = textElements.map((el) => ({
      ...el,
      position: {
        x: el.position.x * scaleX,
        y: el.position.y * scaleY,
      },
    }));

    set({
      activePreset: preset,
      canvasWidth: newWidth,
      canvasHeight: newHeight,
      nftElements: updatedNFTElements,
      textElements: updatedTextElements,
    });
  },

  setCustomDimensions: (width: number, height: number) => {
    set({
      canvasWidth: width,
      canvasHeight: height,
      activePreset: { id: 'custom', name: 'Custom', width, height },
    });
  },

  setBackgroundImage: (imageUrl: string | null, autoFit?: { width: number; height: number }) => {
    if (imageUrl && autoFit) {
      // Calculate scale to cover the canvas (cover mode)
      const { canvasWidth, canvasHeight } = get();
      const imgAspect = autoFit.width / autoFit.height;
      const canvasAspect = canvasWidth / canvasHeight;

      // Use cover: scale so image covers entire canvas
      let scale: number;
      if (imgAspect > canvasAspect) {
        // Image is wider - scale based on height
        scale = canvasHeight / autoFit.height;
      } else {
        // Image is taller - scale based on width
        scale = canvasWidth / autoFit.width;
      }

      set({
        backgroundImage: imageUrl,
        backgroundImageScale: scale,
        backgroundImageOffset: { x: 0, y: 0 },
        nftElements: get().nftElements.map((e) => ({ ...e, useBackgroundLayer: false })),
      });
    } else {
      set({
        backgroundImage: imageUrl,
        backgroundImageScale: 1.0,
        backgroundImageOffset: { x: 0, y: 0 },
        nftElements: get().nftElements.map((e) => ({ ...e, useBackgroundLayer: false })),
      });
    }
  },

  setBackgroundImageScale: (scale: number) => {
    set({ backgroundImageScale: scale });
  },

  setBackgroundImageOffset: (offset: { x: number; y: number }) => {
    set({ backgroundImageOffset: offset });
  },

  setBackgroundColor: (color: string) => {
    set({ backgroundColor: color });
  },

  setViewport: (viewport: Partial<MemeViewport>) => {
    const current = get().viewport;
    set({ viewport: { ...current, ...viewport } });
  },

  resetViewport: () => {
    set({ viewport: DEFAULT_VIEWPORT });
  },

  addNFTElement: (nft: NFTData) => {
    const { nftElements, textElements, canvasWidth, canvasHeight } = get();
    const allZIndexes = [
      ...nftElements.map((e) => e.zIndex),
      ...textElements.map((e) => e.zIndex),
    ];
    const maxZIndex = allZIndexes.length > 0 ? Math.max(...allZIndexes) : 0;

    // Adjust initial scale based on canvas aspect ratio
    // Banner format (wider) needs smaller characters, square gets default
    const aspectRatio = canvasWidth / canvasHeight;
    let initialScale = 0.45;
    if (aspectRatio > 1.5) {
      // Banner/landscape format - scale based on height to fit properly
      initialScale = 0.35;
    } else if (aspectRatio < 0.7) {
      // Portrait/story format - scale based on width
      initialScale = 0.35;
    }

    const newElement: MemeNFTElement = {
      id: generateElementId(),
      nftData: nft,
      position: { x: canvasWidth / 2, y: canvasHeight / 2 },
      scale: initialScale,
      rotation: 0,
      zIndex: maxZIndex + 1,
      selectedLayers: [],
      useBackgroundLayer: !get().backgroundImage && !nftElements.some((e) => e.useBackgroundLayer),
      useFrameLayer: false,
      stickerMode: false,
    };

    set({
      nftElements: [...nftElements, newElement],
      selectedElementId: newElement.id,
      selectedElementType: 'nft',
    });
  },

  removeNFTElement: (elementId: string) => {
    const { nftElements, selectedElementId } = get();
    set({
      nftElements: nftElements.filter((e) => e.id !== elementId),
      selectedElementId: selectedElementId === elementId ? null : selectedElementId,
      selectedElementType: selectedElementId === elementId ? null : get().selectedElementType,
    });
  },

  duplicateNFTElement: (elementId: string) => {
    const { nftElements, textElements } = get();
    const element = nftElements.find((e) => e.id === elementId);
    if (!element) return;

    const allZIndexes = [
      ...nftElements.map((e) => e.zIndex),
      ...textElements.map((e) => e.zIndex),
    ];
    const maxZIndex = Math.max(...allZIndexes);
    const newElement: MemeNFTElement = {
      ...element,
      id: generateElementId(),
      position: {
        x: element.position.x + 50,
        y: element.position.y + 50,
      },
      zIndex: maxZIndex + 1,
    };

    set({
      nftElements: [...nftElements, newElement],
      selectedElementId: newElement.id,
      selectedElementType: 'nft',
    });
  },

  updateNFTElementPosition: (elementId: string, position: { x: number; y: number }) => {
    set({
      nftElements: get().nftElements.map((e) => (e.id === elementId ? { ...e, position } : e)),
    });
  },

  updateNFTElementScale: (elementId: string, scale: number) => {
    set({
      nftElements: get().nftElements.map((e) => (e.id === elementId ? { ...e, scale } : e)),
    });
  },

  updateNFTElementRotation: (elementId: string, rotation: number) => {
    set({
      nftElements: get().nftElements.map((e) => (e.id === elementId ? { ...e, rotation } : e)),
    });
  },

  updateNFTElementZIndex: (elementId: string, zIndex: number) => {
    set({
      nftElements: get().nftElements.map((e) => (e.id === elementId ? { ...e, zIndex } : e)),
    });
  },

  reorderNFTElements: (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;

    const { nftElements } = get();
    const orderedByFront = [...nftElements].sort((a, b) => b.zIndex - a.zIndex);
    const sourceIndex = orderedByFront.findIndex((e) => e.id === sourceId);
    const targetIndex = orderedByFront.findIndex((e) => e.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const [moved] = orderedByFront.splice(sourceIndex, 1);
    orderedByFront.splice(targetIndex, 0, moved);

    const sortedNFTZValues = [...nftElements].map((e) => e.zIndex).sort((a, b) => a - b);
    const zIndexById = new Map<string, number>();
    orderedByFront.forEach((e, index) => {
      zIndexById.set(e.id, sortedNFTZValues[sortedNFTZValues.length - 1 - index]);
    });

    set({
      nftElements: nftElements.map((e) => ({
        ...e,
        zIndex: zIndexById.get(e.id) ?? e.zIndex,
      })),
    });
  },

  updateNFTElementLayers: (
    elementId: string,
    selectedLayers: string[],
    useBackground: boolean,
    useFrame: boolean
  ) => {
    const updatedElements = get().nftElements.map((e) =>
      e.id === elementId
        ? { ...e, selectedLayers, useBackgroundLayer: useBackground, useFrameLayer: useFrame }
        : e
    );

    set({
      nftElements: updatedElements,
    });
  },

  setExclusiveNFTBackground: (elementId: string, enabled: boolean) => {
    // When enabling background for one NFT, disable it for all others
    const updatedElements = get().nftElements.map((e) =>
      e.id === elementId
        ? { ...e, useBackgroundLayer: enabled }
        : enabled
          ? { ...e, useBackgroundLayer: false }
          : e
    );

    set({
      nftElements: updatedElements,
    });
  },

  setAllNFTBackgroundLayers: (enabled: boolean) => {
    set({
      nftElements: get().nftElements.map((e) => ({ ...e, useBackgroundLayer: enabled })),
    });
  },

  updateNFTElementStickerMode: (elementId: string, stickerMode: boolean) => {
    // When sticker mode is enabled:
    // - Automatically disables background and frame layers
    // - Resets position to canvas center
    // - Uses default scale based on canvas aspect ratio (same as when NFT is added)
    // When disabled, it restores background layer (frame stays off by default)
    const { canvasWidth, canvasHeight } = get();

    // Calculate default scale based on canvas aspect ratio (same logic as addNFTElement)
    const aspectRatio = canvasWidth / canvasHeight;
    let defaultScale = 0.45;
    if (aspectRatio > 1.5) {
      defaultScale = 0.35;
    } else if (aspectRatio < 0.7) {
      defaultScale = 0.35;
    }

    set({
      nftElements: get().nftElements.map((e) =>
        e.id === elementId
          ? {
              ...e,
              stickerMode,
              useBackgroundLayer: stickerMode ? false : true,
              useFrameLayer: stickerMode ? false : e.useFrameLayer,
              // Reset position to center and use default scale when sticker mode is enabled
              position: stickerMode ? { x: canvasWidth / 2, y: canvasHeight / 2 } : e.position,
              scale: stickerMode ? defaultScale : e.scale,
            }
          : e
      ),
    });
  },

  addTextElement: (text: string) => {
    const { nftElements, textElements, canvasWidth, canvasHeight } = get();
    const allZIndexes = [
      ...nftElements.map((e) => e.zIndex),
      ...textElements.map((e) => e.zIndex),
    ];
    const maxZIndex = allZIndexes.length > 0 ? Math.max(...allZIndexes) : 0;

    const newElement: MemeTextElement = {
      id: generateElementId(),
      text,
      position: { x: canvasWidth / 2, y: canvasHeight / 2 },
      fontSize: 48,
      fontFamily: 'Impact',
      color: '#ffffff',
      strokeColor: '#000000',
      strokeWidth: 2,
      rotation: 0,
      zIndex: maxZIndex + 1,
    };

    set({
      textElements: [...textElements, newElement],
      selectedElementId: newElement.id,
      selectedElementType: 'text',
    });
  },

  removeTextElement: (elementId: string) => {
    const { textElements, selectedElementId } = get();
    set({
      textElements: textElements.filter((e) => e.id !== elementId),
      selectedElementId: selectedElementId === elementId ? null : selectedElementId,
      selectedElementType: selectedElementId === elementId ? null : get().selectedElementType,
    });
  },

  updateTextElement: (elementId: string, updates: Partial<Omit<MemeTextElement, 'id'>>) => {
    set({
      textElements: get().textElements.map((e) =>
        e.id === elementId ? { ...e, ...updates } : e
      ),
    });
  },

  selectElement: (elementId: string | null, elementType: 'nft' | 'text' | null) => {
    if (elementType === 'text' && elementId) {
      const textElements = get().textElements;
      const shouldClearPlaceholder = textElements.some(
        (el) =>
          el.id === elementId &&
          el.text.trim().toUpperCase() === DEFAULT_TEXT_PLACEHOLDER
      );

      if (shouldClearPlaceholder) {
        set({
          textElements: textElements.map((el) =>
            el.id === elementId ? { ...el, text: '' } : el
          ),
          selectedElementId: elementId,
          selectedElementType: elementType,
        });
        return;
      }
    }

    set({ selectedElementId: elementId, selectedElementType: elementType });
  },

  bringToFront: (elementId: string, elementType: 'nft' | 'text') => {
    const { nftElements, textElements } = get();
    const allZIndexes = [
      ...nftElements.map((e) => e.zIndex),
      ...textElements.map((e) => e.zIndex),
    ];
    const maxZIndex = Math.max(...allZIndexes);

    if (elementType === 'nft') {
      set({
        nftElements: nftElements.map((e) =>
          e.id === elementId ? { ...e, zIndex: maxZIndex + 1 } : e
        ),
      });
    } else {
      set({
        textElements: textElements.map((e) =>
          e.id === elementId ? { ...e, zIndex: maxZIndex + 1 } : e
        ),
      });
    }
  },

  sendToBack: (elementId: string, elementType: 'nft' | 'text') => {
    const { nftElements, textElements } = get();
    const allZIndexes = [
      ...nftElements.map((e) => e.zIndex),
      ...textElements.map((e) => e.zIndex),
    ];
    const minZIndex = Math.min(...allZIndexes);

    if (elementType === 'nft') {
      set({
        nftElements: nftElements.map((e) =>
          e.id === elementId ? { ...e, zIndex: minZIndex - 1 } : e
        ),
      });
    } else {
      set({
        textElements: textElements.map((e) =>
          e.id === elementId ? { ...e, zIndex: minZIndex - 1 } : e
        ),
      });
    }
  },

  resetCanvas: () => {
    set({
      canvasWidth: DEFAULT_MEME_PRESET.width,
      canvasHeight: DEFAULT_MEME_PRESET.height,
      activePreset: DEFAULT_MEME_PRESET,
      backgroundImage: null,
      backgroundImageScale: 1.0,
      backgroundImageOffset: { x: 0, y: 0 },
      backgroundColor: '#1a1a2e',
      nftElements: [],
      textElements: [],
      selectedElementId: null,
      selectedElementType: null,
      isDragging: false,
      isResizing: false,
      resizeHandle: null,
      viewport: DEFAULT_VIEWPORT,
    });
    get()._clearHistory();
  },

  setDragging: (isDragging: boolean) => {
    if (isDragging) {
      get()._saveCheckpoint();
      get()._pauseUndo();
    }
    set({ isDragging });
    if (!isDragging) {
      get()._resumeUndo();
    }
  },

  setResizing: (isResizing: boolean, handle: string | null = null) => {
    if (isResizing) {
      get()._saveCheckpoint();
      get()._pauseUndo();
    }
    set({ isResizing, resizeHandle: handle });
    if (!isResizing) {
      get()._resumeUndo();
    }
  },
}), { selectedElementId: null, selectedElementType: null } as Partial<MemeBase>));
